import { FormDialog } from '@trellis/roots/overlays'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { Skeleton } from '@trellis/roots/skeleton'
import { Typography } from '@trellis/roots/typography'
import { resolveClaimableAmount } from '@trellis/shared'
import type { ExpenseResponse } from '@trellis/shared'
import { useState } from 'react'

import { useInvoicesQuery } from '@/hooks'
import { formatCurrency } from '@/lib/format'

interface BillExpenseDialogProps {
  expense: ExpenseResponse | null
  isPending: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (invoiceId: string) => void
}

export function BillExpenseDialog({
  expense,
  isPending,
  onOpenChange,
  onConfirm,
}: BillExpenseDialogProps) {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')

  const invoicesQuery = useInvoicesQuery({
    status: 'DRAFT',
    clientId: expense?.clientId ?? undefined,
    limit: 100,
  })

  const draftInvoices = invoicesQuery.data?.pages.flatMap((page) => page.data ?? []) ?? []

  const billedAmount = expense
    ? resolveClaimableAmount(
        expense.totalCost,
        expense.claimableAmount,
        expense.claimableAmountType,
      ) * expense.quantity
    : 0

  function handleConfirm() {
    if (!selectedInvoiceId) return
    onConfirm(selectedInvoiceId)
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) setSelectedInvoiceId('')
    onOpenChange(isOpen)
  }

  return (
    <FormDialog
      open={expense !== null}
      onOpenChange={handleOpenChange}
      title="Add to Invoice"
      description={
        <>
          Select a draft invoice for{' '}
          {expense?.clientName ? (
            <span className="text-foreground font-medium">{expense.clientName}</span>
          ) : (
            'this client'
          )}{' '}
          to add this reimbursement ({formatCurrency(billedAmount)}) as a line item.
        </>
      }
      submitLabel="Add to invoice"
      pendingLabel="Adding…"
      isPending={isPending}
      submitDisabled={!selectedInvoiceId || draftInvoices.length === 0}
      onSubmit={handleConfirm}
    >
      <div className="space-y-4">
        {invoicesQuery.isLoading ? (
          <Skeleton
            variant="rect"
            className="h-9 w-full"
            role="status"
            aria-label="Loading invoices"
          />
        ) : draftInvoices.length === 0 ? (
          <Typography variant="body" color="muted">
            No draft invoices found for this client.
          </Typography>
        ) : (
          <Select value={selectedInvoiceId} onValueChange={setSelectedInvoiceId}>
            <SelectTrigger aria-label="Select an invoice">
              <SelectValue placeholder="Select an invoice…" />
            </SelectTrigger>
            <SelectContent>
              {draftInvoices.map((invoice) => (
                <SelectItem key={invoice.id} value={invoice.id}>
                  {invoice.invoiceNumber} — {formatCurrency(invoice.total)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </FormDialog>
  )
}
